import { memo } from "react";

interface WidgetPlaceholderProps {
  title: string;
  subtitle?: string;
  /**
   * minHeight for the skeleton slot. Accepts a CSS length (`"240px"`, `"20rem"`)
   * or a number interpreted as pixels. Defaults to WIDGET_PLACEHOLDER_MIN_HEIGHT.
   */
  height?: number | string;
  /**
   * Optional override for the screen-reader announcement.
   * Defaults to `"Loading {title}"`. The visible `<h2>` is `aria-hidden` so
   * screen readers only hear the status once — not title + sr-only duplicate.
   * Keep it short — assistive tech repeats it per poll.
   */
  ariaLabel?: string;
}

// Default minHeight matches the shortest rendered widget (confidence
// distribution / WIDGET_HEIGHT_SM in Overview.tsx). Without it the skeleton
// collapses to 0px when the grid row has no other content, causing layout
// shift when data resolves. Exported so Overview.tsx can align against the
// same constant instead of duplicating the magic number.
export const WIDGET_PLACEHOLDER_MIN_HEIGHT = 240;

// Loading skeleton for dashboard widgets. Exported as a memoized component
// because it is rendered inside parents that re-render on every React-Query
// poll tick — re-mounting the spinner causes visible flicker.
export const WidgetPlaceholder = memo(function WidgetPlaceholder({
  title,
  subtitle,
  height,
  ariaLabel,
}: WidgetPlaceholderProps) {
  // `??` covers the undefined-default; numbers pass through to React which
  // appends `px` automatically, strings (`"20rem"`, `"240px"`) are used
  // verbatim. The previous ternary had a redundant string→string branch.
  const resolvedMinHeight = height ?? WIDGET_PLACEHOLDER_MIN_HEIGHT;
  // Reserve the subtitle slot even when absent so the row that appears after
  // data loads doesn't shift vertically. `invisible` keeps the box in flow
  // without announcing it.
  return (
    <div
      className="adp-card flex flex-col"
      style={{ minHeight: resolvedMinHeight }}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel ?? `Loading ${title}`}
    >
      <div className="mb-3">
        {/* Visible title is hidden from AT — the parent's aria-label already
            announces the widget identity, so repeating the title here would
            cause a "Loading X … X" double-read. */}
        <h2
          className="text-[11px] text-mono uppercase tracking-wider text-foreground"
          aria-hidden="true"
        >
          {title}
        </h2>
        <p
          className={`text-xs text-muted-foreground mt-1 ${subtitle ? "" : "invisible"}`}
          aria-hidden="true"
        >
          {subtitle ?? "\u00a0"}
        </p>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="adp-spinner w-6 h-6" aria-hidden="true" />
      </div>
    </div>
  );
});
